<template>
	<Card v-if="visible" :bordered="false" class="paas-list-status">
		<div :class="['paas-list-status-body', { 'has-metrics': showCounts }]">
			<div class="paas-list-status-main">
				<Button
					:type="openButtonType"
					class="paas-list-status-open"
					:class="{ 'new-paas-results': paasStore.newTask }"
					@click="onOpen"
				>
					{{ openButtonLabel }}
				</Button>
				<div v-if="warning.kind === 'stale'" class="paas-coords-stale-alert paas-list-status-warn-below">
					<WarningOutlined class="paas-coords-stale-icon" />
					<span>{{ $t('paas.listPanel.coordsStaleDays', warning.days) }}</span>
				</div>
				<div v-else-if="warning.kind === 'never'" class="paas-coords-stale-alert paas-list-status-warn-below">
					<WarningOutlined class="paas-coords-stale-icon" />
					<span>{{ $t('paas.listPanel.never') }}</span>
				</div>
				<div v-if="showCounts && problemsCount > 0" class="paas-coords-stale-alert paas-list-status-warn-below paas-list-status-problems">
					<WarningOutlined class="paas-coords-stale-icon" />
					<span>{{ $t('paas.listPanel.problemsWithStations', problemsCount) }}</span>
				</div>
			</div>
			<div v-if="showCounts" class="paas-list-status-metrics-block">
				<Row class="paas-list-status-metrics" :gutter="24">
					<Col>
						<Statistic :title="$t('paas.listPanel.sentTotal')" :value="totalCount" />
					</Col>
					<Col>
						<Statistic :value="sentWithMeasurementsCount">
							<template #title>
								<span class="paas-sent-wm-title">
									{{ $t('paas.listPanel.sentWithMeasurements') }}
									<Tooltip :title="$t('paas.listPanel.sentWithMeasurementsTooltip')">
										<QuestionCircleOutlined class="paas-metric-help-icon" />
									</Tooltip>
								</span>
							</template>
						</Statistic>
					</Col>
					<Col>
						<Statistic :title="$t('paas.listPanel.solutions')" :value="solutionCount" />
					</Col>
				</Row>
			</div>
		</div>
	</Card>
</template>

<script>
import { computed, onMounted, watch } from 'vue';
import { Button, Card, Row, Col, Statistic, Tooltip } from 'ant-design-vue';
import { WarningOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Consts from '@/api/Consts';
import { useBaseStationsStore } from '@/stores/baseStations';
import { useLicenseStore } from '@/stores/licenseStore';
import { usePaasStore } from '@/stores/paas';

import { getPaasCoordsWarning } from '@/components/paas/paasListStatusUtils';


export default {
	components: {
		Button,
		Card,
		Row,
		Col,
		Statistic,
		Tooltip,
		WarningOutlined,
		QuestionCircleOutlined,
	},
	setup() {
		const { t } = useI18n();
		const router = useRouter();
		const paasStore = usePaasStore();
		const licenseStore = useLicenseStore();
		const baseStationsStore = useBaseStationsStore();
		const visible = computed(() => licenseStore.allowPaas && baseStationsStore.items.length > 0);
		const showCounts = computed(() => paasStore.showProgress && paasStore.totalCount > 0);
		const openButtonLabel = computed(() => {
			if (paasStore.newTask) {
				return t('paas.newResultsLabel');
			}
			if (paasStore.showProgress && paasStore.finishedCount < paasStore.totalCount) {
				return t('paas.progressHint');
			}
			return t('paas.header');
		});
		const openButtonType = computed(() => (paasStore.newTask ? 'primary' : 'default'));
		const warning = computed(() => getPaasCoordsWarning(
			baseStationsStore.items
				.filter(item => !item.deleted && 'paas_coords_age_days' in item)
				.map(item => item.paas_coords_age_days),
		));
		if (licenseStore.allowPaas) {
			onMounted(() => {
				paasStore.updatePaasData();
			});
		}
		watch(() => licenseStore.allowPaas, value => {
			if (value) {
				paasStore.updatePaasData();
			}
		});
		const onOpen = () => router.push({ name: 'stats-type', params: { id: 'coordinates-correction' } });
		return {
			visible,
			showCounts,
			openButtonLabel,
			openButtonType,
			warning,
			paasStore,
			totalCount: computed(() => paasStore.totalCount),
			sentWithMeasurementsCount: computed(() => paasStore.sentWithMeasurementsCount),
			solutionCount: computed(() => paasStore.solutionCount),
			problemsCount: computed(() => paasStore.taskProblems.length),
			onOpen,
			colorSuccess: Consts.COLORS.success,
			colorSuccessDark: Consts.COLORS.successDark,
			colorWarningDark: Consts.COLORS.warningDark,
		};
	},
};
</script>

<style scoped>
.paas-list-status :deep(.ant-card-body) {
	padding: 11px 12px 10px;
}

.paas-list-status-body {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	align-items: flex-start;
}

.paas-list-status-body.has-metrics {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	column-gap: 24px;
	row-gap: 12px;
	align-items: start;
}

.paas-list-status-main {
	flex: 1 1 12rem;
	min-width: 0;
}

.paas-list-status-body.has-metrics .paas-list-status-main {
	grid-column: 1;
	min-width: 0;
	max-width: 100%;
}

.paas-list-status-metrics-block {
	flex: 0 1 auto;
	margin-left: auto;
	max-width: 100%;
}

.paas-list-status-body.has-metrics .paas-list-status-metrics-block {
	grid-column: 2;
	grid-row: 1;
	margin-left: 0;
	justify-self: end;
}

@media (max-width: 640px) {
	.paas-list-status-body.has-metrics {
		grid-template-columns: minmax(0, 1fr);
	}

	.paas-list-status-body.has-metrics .paas-list-status-metrics-block {
		grid-column: 1;
		grid-row: 2;
		justify-self: start;
	}
}

.paas-list-status-metrics {
	flex: 0 0 auto;
	width: auto;
}

.paas-list-status-metrics :deep(.ant-row) {
	width: auto;
	flex-wrap: nowrap;
}

.paas-sent-wm-title {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.paas-metric-help-icon {
	color: rgba(0, 0, 0, 0.45);
	font-size: 12px;
	cursor: help;
}

.paas-list-status-problems {
	white-space: normal;
}

.paas-list-status-warn-below {
	margin-top: 8px;
}

.paas-coords-stale-alert {
	line-height: 1.4;
	color: v-bind(colorWarningDark);
}

.paas-coords-stale-icon {
	color: v-bind(colorWarningDark);
	margin-right: 4px;
}

.paas-list-status-open {
	max-width: 100%;
	white-space: normal;
	height: auto;
	background-color: v-bind(colorSuccessDark);
	border-color: v-bind(colorSuccessDark);
}

.new-paas-results:hover,
.new-paas-results:focus {
	background-color: v-bind(colorSuccess);
	border-color: v-bind(colorSuccess);
}
</style>
